import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const SCROLL_INERTIA = {
  maxOffset: 8,
  maxScrollDelta: 40,
  ignoredScrollJump: 160,
  impulse: 2.25,
  maxVelocity: 105,
  spring: 205,
  damping: 21,
  reversalRetention: 0.42,
  offsetRestThreshold: 0.025,
  velocityRestThreshold: 0.25,
};

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {HTMLElement | null} */
  #stickyPositionTarget = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** @type {number | null} */
  #inertiaRafId = null;

  /** @type {number | null} */
  #inertiaRebaseRafId = null;

  /** @type {number | null} */
  #inertiaLastTimestamp = null;

  /** @type {number} */
  #inertiaLastScrollTop = 0;

  /** @type {number} */
  #inertiaOffset = 0;

  /** @type {number} */
  #inertiaVelocity = 0;

  /** @type {boolean} */
  #suppressInertia = false;

  /** @type {MutationObserver | null} */
  #inertiaInteractionObserver = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this.#getStickyPositionTarget());
  };

  /**
   * The component itself moves for scroll inertia, so sticky measurements use
   * the stationary Shopify section wrapper instead.
   * @returns {HTMLElement}
   */
  #getStickyPositionTarget = () => {
    if (this.#stickyPositionTarget) return this.#stickyPositionTarget;

    const section = this.parentElement;
    if (
      this.hasAttribute('data-scroll-inertia') &&
      scrollContainerMediaQuery.matches &&
      section instanceof HTMLElement &&
      section.offsetHeight > 0
    ) {
      this.#stickyPositionTarget = section;
    } else {
      this.#stickyPositionTarget = this;
    }

    return this.#stickyPositionTarget;
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    if (this.hasAttribute('data-scroll-inertia')) this.#scheduleInertiaRebase();
    this.#lastScrollTop = getScrollTop();

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#stickyPositionTarget = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      const scrollTop = getScrollTop();
      this.#updateScrollState(scrollTop);
      if (this.hasAttribute('data-scroll-inertia')) this.#updateScrollInertia(scrollTop);
    });
  };

  /**
   * @param {number} scrollTop
   */
  #updateScrollState = (scrollTop) => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const headerTop = this.#getStickyPositionTarget().getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Whether the optional inertia effect is safe for the current header state.
   * Mobile/drawer headers stay fixed so their viewport-positioned panels keep
   * their normal containing block.
   * @returns {boolean}
   */
  #isScrollInertiaEnabled = () => {
    return (
      this.hasAttribute('data-scroll-inertia') &&
      this.getAttribute('sticky') === 'always' &&
      scrollContainerMediaQuery.matches &&
      !reducedMotionMediaQuery.matches &&
      this.dataset.menuStyle === 'menu' &&
      !document.hidden
    );
  };

  /**
   * Keep moving hit targets still while menus or keyboard interactions are active.
   * @returns {boolean}
   */
  #hasBlockingHeaderInteraction = () => {
    return (
      this.querySelector(':focus-visible, [aria-expanded="true"], details[open], dialog[open]') !== null
    );
  };

  /**
   * Converts coalesced scroll distance into a small velocity impulse.
   * @param {number} scrollTop
   */
  #updateScrollInertia = (scrollTop) => {
    const scrollDelta = scrollTop - this.#inertiaLastScrollTop;
    this.#inertiaLastScrollTop = scrollTop;

    if (
      this.#suppressInertia ||
      !this.#isScrollInertiaEnabled() ||
      this.#hasBlockingHeaderInteraction()
    ) {
      this.#resetScrollInertia(false);
      return;
    }

    if (Math.abs(scrollDelta) < 0.1) return;

    // Ignore restoration, anchor navigation, and other programmatic jumps.
    if (Math.abs(scrollDelta) > SCROLL_INERTIA.ignoredScrollJump) {
      this.#resetScrollInertia(false);
      return;
    }

    const clampedDelta = Math.max(
      -SCROLL_INERTIA.maxScrollDelta,
      Math.min(SCROLL_INERTIA.maxScrollDelta, scrollDelta)
    );
    const impulse = -clampedDelta * SCROLL_INERTIA.impulse;

    if (this.#inertiaVelocity !== 0 && Math.sign(impulse) !== Math.sign(this.#inertiaVelocity)) {
      this.#inertiaVelocity *= SCROLL_INERTIA.reversalRetention;
    }

    this.#inertiaVelocity = Math.max(
      -SCROLL_INERTIA.maxVelocity,
      Math.min(SCROLL_INERTIA.maxVelocity, this.#inertiaVelocity + impulse)
    );

    if (this.#inertiaRafId === null) {
      this.#inertiaLastTimestamp = performance.now();
      this.#inertiaRafId = requestAnimationFrame(this.#animateScrollInertia);
    }
  };

  /**
   * Advances a damped spring toward its zero-offset resting position.
   * @param {DOMHighResTimeStamp} timestamp
   */
  #animateScrollInertia = (timestamp) => {
    this.#inertiaRafId = null;

    if (!this.#isScrollInertiaEnabled() || this.#hasBlockingHeaderInteraction()) {
      this.#resetScrollInertia();
      return;
    }

    const elapsed = Math.min(Math.max((timestamp - (this.#inertiaLastTimestamp ?? timestamp)) / 1000, 0), 1 / 30);
    this.#inertiaLastTimestamp = timestamp;

    // Small substeps keep the spring consistent on both 60 Hz and 120 Hz displays.
    const substeps = Math.max(1, Math.ceil(elapsed / (1 / 120)));
    const timestep = elapsed / substeps;

    for (let index = 0; index < substeps; index += 1) {
      const acceleration =
        -SCROLL_INERTIA.spring * this.#inertiaOffset - SCROLL_INERTIA.damping * this.#inertiaVelocity;
      this.#inertiaVelocity += acceleration * timestep;
      this.#inertiaOffset += this.#inertiaVelocity * timestep;

      if (Math.abs(this.#inertiaOffset) > SCROLL_INERTIA.maxOffset) {
        this.#inertiaOffset = Math.sign(this.#inertiaOffset) * SCROLL_INERTIA.maxOffset;

        if (Math.sign(this.#inertiaVelocity) === Math.sign(this.#inertiaOffset)) {
          this.#inertiaVelocity = 0;
        }
      }
    }

    if (
      Math.abs(this.#inertiaOffset) < SCROLL_INERTIA.offsetRestThreshold &&
      Math.abs(this.#inertiaVelocity) < SCROLL_INERTIA.velocityRestThreshold
    ) {
      this.#resetScrollInertia();
      return;
    }

    this.style.setProperty('--header-scroll-inertia-y', `${this.#inertiaOffset.toFixed(3)}px`);
    this.dataset.scrollInertiaActive = '';
    this.#inertiaRafId = requestAnimationFrame(this.#animateScrollInertia);
  };

  /**
   * Removes the compositor layer completely at rest and optionally syncs the
   * scroll baseline so the next user gesture starts without a false impulse.
   * @param {boolean} syncScrollTop
   */
  #resetScrollInertia = (syncScrollTop = true) => {
    const wasRendered = this.hasAttribute('data-scroll-inertia-active');

    if (this.#inertiaRafId !== null) {
      cancelAnimationFrame(this.#inertiaRafId);
      this.#inertiaRafId = null;
    }

    this.#inertiaLastTimestamp = null;
    this.#inertiaOffset = 0;
    this.#inertiaVelocity = 0;

    if (wasRendered) {
      this.style.removeProperty('--header-scroll-inertia-y');
      this.removeAttribute('data-scroll-inertia-active');
    }

    if (syncScrollTop) this.#inertiaLastScrollTop = getScrollTop();
  };

  /**
   * Scroll restoration runs in the next frame. Waiting one additional frame
   * rebases after it and prevents back/forward navigation from kicking the spring.
   */
  #scheduleInertiaRebase = () => {
    this.#suppressInertia = true;
    this.#resetScrollInertia();

    if (this.#inertiaRebaseRafId !== null) cancelAnimationFrame(this.#inertiaRebaseRafId);

    this.#inertiaRebaseRafId = requestAnimationFrame(() => {
      this.#inertiaRebaseRafId = requestAnimationFrame(() => {
        this.#inertiaRebaseRafId = null;
        this.#inertiaLastScrollTop = getScrollTop();
        this.#lastScrollTop = this.#inertiaLastScrollTop;
        this.#suppressInertia = false;
      });
    });
  };

  #handleInertiaPreferenceChange = () => {
    this.#scheduleInertiaRebase();
  };

  #handlePageHide = () => {
    this.#suppressInertia = true;
    this.#resetScrollInertia();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#handlePageHide();
    } else {
      this.#scheduleInertiaRebase();
    }
  };

  #handleHeaderInteraction = () => {
    this.#resetScrollInertia();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#suppressInertia = false;
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#lastScrollTop = getScrollTop();
      this.#inertiaLastScrollTop = this.#lastScrollTop;
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }

    if (this.hasAttribute('data-scroll-inertia')) {
      reducedMotionMediaQuery.addEventListener('change', this.#handleInertiaPreferenceChange);
      window.addEventListener('pageshow', this.#scheduleInertiaRebase);
      window.addEventListener('popstate', this.#scheduleInertiaRebase);
      window.addEventListener('pagehide', this.#handlePageHide);
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
      this.addEventListener('pointerdown', this.#handleHeaderInteraction);
      this.addEventListener('focusin', this.#handleHeaderInteraction);

      this.#inertiaInteractionObserver = new MutationObserver(() => {
        if (!this.#isScrollInertiaEnabled() || this.#hasBlockingHeaderInteraction()) {
          this.#resetScrollInertia();
        } else {
          this.#inertiaLastScrollTop = getScrollTop();
        }
      });
      this.#inertiaInteractionObserver.observe(this, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-menu-style', 'aria-expanded', 'open'],
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#stickyPositionTarget = null;
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    reducedMotionMediaQuery.removeEventListener('change', this.#handleInertiaPreferenceChange);
    window.removeEventListener('pageshow', this.#scheduleInertiaRebase);
    window.removeEventListener('popstate', this.#scheduleInertiaRebase);
    window.removeEventListener('pagehide', this.#handlePageHide);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.removeEventListener('pointerdown', this.#handleHeaderInteraction);
    this.removeEventListener('focusin', this.#handleHeaderInteraction);
    this.#inertiaInteractionObserver?.disconnect();
    this.#inertiaInteractionObserver = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#inertiaRebaseRafId !== null) {
      cancelAnimationFrame(this.#inertiaRebaseRafId);
      this.#inertiaRebaseRafId = null;
    }
    this.#resetScrollInertia();
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
